/**
 * Logging Service
 * 
 * Centralized logging service for Pivot.is application.
 * Provides structured logging with multiple output targets and trace ID support.
 * 
 * Usage:
 * ```typescript
 * import { log, trace } from '../services/logging/index.js';
 * 
 * const traceId = trace.start('operation');
 * log.info('Operation started', { id: '123' }, traceId, 'MyModule');
 * log.error('Operation failed', { error }, traceId, 'MyModule');
 * trace.end(traceId);
 * ```
 */

import { Logger } from './logger.js';
import { LogLevel } from './types.js';
import * as trace from './trace.js';

/**
 * Global logger instance
 * Configured with defaults: console enabled, storage disabled
 * Log level: Warn in dev, Error in prod (Debug level was causing 15,000+ logs on startup)
 */
export const log = new Logger({
  level: import.meta.env.DEV ? LogLevel.Warn : LogLevel.Error,
  enableConsole: true,
  enableStorage: true, // Enable storage for debugger app
  storageRetention: 1000, // Keep last 1000 logs
  enableRemote: false,
});

/**
 * Re-export trace functions
 */
export { trace };

/**
 * Re-export types for use in other modules
 */
export type { LogEntry, LogTarget, LoggerConfig } from './types.js';
export { LogLevel } from './types.js';


